import { SIM_HZ } from '../../data/tuning';
import { TickPhase, type CoreWorld } from '../tick';
import { createRhythmState, rhythmView, stepRhythm, type RhythmClockState, type RhythmConfig, type RhythmPulses } from './rhythm-clock';

/**
 * The rhythm clock inside a core world (issue #109): a system of phase 1 that computes the pulses of the tick, keeps
 * them in `current` for the patterns of the bosses that run later in the same tick (JEFE-2, #110) and announces
 * them with `RHYTHM_BEAT` and `RHYTHM_CLAVE_HIT`. The phase is a function of the ticks of the simulation, so a
 * pause freezes it and a long frame that runs several ticks emits each pulse once.
 */
export interface RhythmHandle {
  config: RhythmConfig;
  state: RhythmClockState;
  /** The pulses of the tick being run (or the last one). */
  current: RhythmPulses | null;
  view(): ReturnType<typeof rhythmView>;
}

export function attachRhythm(world: CoreWorld, config: RhythmConfig): RhythmHandle {
  const handle: RhythmHandle = {
    config,
    state: createRhythmState(),
    current: null,
    view: () => rhythmView(handle.config, handle.state),
  };
  world.systems.register(TickPhase.Input, (current, context) => {
    // `world.tick` counts from 1 inside the tick: the first tick of the simulation is tick 0 of the clock.
    const tick = current.tick - 1;
    const pulses = stepRhythm(handle.config, handle.state, tick);
    handle.current = pulses;
    if (pulses.beat !== null) context.emit({ type: 'RHYTHM_BEAT', beat: pulses.beat, beatTick: tick });
    if (pulses.claveHit) context.emit({ type: 'RHYTHM_CLAVE_HIT', hit: pulses.claveHit.hit, cycle: pulses.claveHit.cycle, step: pulses.claveHit.step, beatTick: tick });
  });
  return handle;
}

/** Output latency in seconds as ticks of the simulation. */
export const latencyTicksOf = (seconds: number): number => Math.round(Math.max(0, seconds) * SIM_HZ);
